"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TransactionAttachmentTable = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const compliance_pool_types_1 = require("../../compliance-pool-types");
const common_styled_components_1 = require("../../../common-styled-components");
const timeUtils_1 = require("../../../../util/timeUtils");
const amountUtils_1 = require("../../../../util/amountUtils");
const TransactionAttachmentUploader_1 = __importDefault(require("../TransactionAttachments/TransactionAttachmentUploader"));
const TransactionAttachmentEditor_1 = __importDefault(require("../TransactionAttachments/TransactionAttachmentEditor"));
const react_1 = require("react");
const Modal_1 = require("../Modal");
const useEdit_1 = __importDefault(require("../../../../hooks/useEdit"));
const isNullish_1 = require("../../../../util/isNullish");
const styled_components_1 = __importDefault(require("styled-components"));
const theme_1 = require("../../../../theme");
const ExportCsvButton_1 = require("../ExportCsvButton");
const maxAttachmentFileSize = 30 * 1024 * 1024 - 500;
const TableWrapper = styled_components_1.default.div `
  position: relative;
`;
const DropOverlay = styled_components_1.default.div `
  position: absolute;
  inset: 0;
  border: 2px dashed ${theme_1.theme.colors.blue};
  border-radius: 8px;
  background-color: ${theme_1.theme.colors.blue}1a;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: ${({ $visible }) => ($visible ? "all" : "none")};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.15s;
  z-index: 10;
`;
const DropOverlayText = styled_components_1.default.span `
  font-size: 1rem;
  color: ${theme_1.theme.colors.blue};
  font-weight: 500;
  pointer-events: none;
`;
const SelectionError = styled_components_1.default.p `
  font-size: 0.75rem;
  color: ${theme_1.theme.colors.red};
  margin: ${theme_1.theme.spacing(1)} 0 0;
`;
const tableColumns = (attachmentType, isInAdminTools) => {
    const base = [{ key: "name", label: "File", sortableKey: "name" }];
    let specific = [];
    switch (attachmentType) {
        case 1: // Periodical Statement
            specific = [
                { key: "period_start", label: "Period start" },
                { key: "period_end", label: "Period end" },
                { key: "energy_mj", label: "Energy" },
                { key: "intensity", label: "Intensity" },
                { key: "balance_tco2e", label: "Balance" },
                { key: "available_tco2e", label: "Available" },
            ];
            break;
        case 2: // Bunker Delivery Note
            specific = [
                { key: "date", label: "Date" },
                { key: "port", label: "Port" },
                { key: "fuel_type_id", label: "Fuel type" },
                { key: "amount_t", label: "Amount" },
                { key: "price_per_ton", label: "PPU" },
                { key: "bio_premium_per_ton", label: "Bio premium" },
                { key: "periodical_statement_id", label: "Periodical statements" },
                { key: "proof_of_sustainability_notes", label: "POS notes" },
            ];
            break;
        default:
            specific = [];
    }
    const toolsCol = { key: "tools", label: "Tools" };
    const cols = [...base, ...specific, toolsCol].filter((c) => c.key !== "tools" || isInAdminTools === true);
    return cols;
};
const CellContent = ({ file, contentKey, isInAdminTools, removeAttachment, editAttachment, periodicalStatementOptions, }) => {
    if (contentKey === "tools" && isInAdminTools) {
        return ((0, jsx_runtime_1.jsxs)("div", { children: [editAttachment && ((0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => editAttachment(file), children: "Edit" })), removeAttachment && ((0, jsx_runtime_1.jsx)(common_styled_components_1.SmallButton, { onClick: () => removeAttachment(file), children: "Remove" }))] }));
    }
    const raw = file[contentKey];
    if ((0, isNullish_1.isNullish)(raw))
        return (0, jsx_runtime_1.jsx)("div", {});
    switch (contentKey) {
        case "name":
            return (0, jsx_runtime_1.jsx)("div", { children: file.name });
        case "period_start":
            return (() => {
                const d = new Date(raw);
                return ((0, jsx_runtime_1.jsx)("div", { children: isNaN(d.getTime()) ? String(raw) : (0, timeUtils_1.formatDate)(d, true) }));
            })();
        case "period_end":
            return (() => {
                const d = new Date(raw);
                return ((0, jsx_runtime_1.jsx)("div", { children: isNaN(d.getTime()) ? String(raw) : (0, timeUtils_1.formatDate)(d, true) }));
            })();
        case "date":
            return (() => {
                const d = new Date(raw);
                return ((0, jsx_runtime_1.jsx)("div", { children: isNaN(d.getTime()) ? String(raw) : (0, timeUtils_1.formatDate)(d, true) }));
            })();
        case "port":
            if ((0, compliance_pool_types_1.isBunkerDeliveryNote)(file) && file.port) {
                return (0, jsx_runtime_1.jsx)("div", { children: file.port.name });
            }
            return (0, jsx_runtime_1.jsx)("div", {});
        case "energy_mj":
        case "intensity":
        case "balance_tco2e":
        case "available_tco2e":
        case "amount_t":
        case "price_per_ton":
        case "bio_premium_per_ton":
            return (() => {
                if (typeof raw === "number") {
                    const digits = Number.isInteger(raw) ? undefined : 2;
                    return (0, jsx_runtime_1.jsx)("div", { children: (0, amountUtils_1.formatNumber)(raw, digits) ?? String(raw) });
                }
                return (0, jsx_runtime_1.jsx)("div", { children: String(raw) });
            })();
        case "periodical_statement_id":
            return ((0, jsx_runtime_1.jsx)("div", { children: periodicalStatementOptions?.find((o) => o.value === Number(raw))
                    ?.label ?? String(raw) }));
        case "proof_of_sustainability_notes":
            return ((0, jsx_runtime_1.jsx)("div", { children: Array.isArray(raw)
                    ? raw.map((note) => note.name).join(", ")
                    : String(raw) }));
        default: {
            return (0, jsx_runtime_1.jsx)("div", { children: String(raw) });
        }
    }
};
const TransactionAttachmentTable = ({ transactionAttachments, attachmentType, isInAdminTools, shipId, refresh, periodicalStatementOptions, selectedYear, }) => {
    const [modal, setModal] = (0, react_1.useState)({});
    const [editingAttachment, setEditingAttachment] = (0, react_1.useState)(null);
    const [isEditorOpen, setIsEditorOpen] = (0, react_1.useState)(false);
    const [_deleteStatus, executeDelete] = (0, useEdit_1.default)("admin/delete_transaction_attachment.json", refresh);
    const [isDragging, setIsDragging] = (0, react_1.useState)(false);
    const [isUploaderOpen, setIsUploaderOpen] = (0, react_1.useState)(false);
    const [droppedFiles, setDroppedFiles] = (0, react_1.useState)([]);
    const [selectionError, setSelectionError] = (0, react_1.useState)(null);
    const dragCounter = (0, react_1.useRef)(0);
    const handleDragEnter = (0, react_1.useCallback)((e) => {
        e.preventDefault();
        dragCounter.current++;
        setIsDragging(true);
    }, []);
    const handleDragOver = (0, react_1.useCallback)((e) => {
        e.preventDefault();
    }, []);
    const handleDragLeave = (0, react_1.useCallback)(() => {
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDragging(false);
        }
    }, []);
    const handleDrop = (0, react_1.useCallback)((e) => {
        e.preventDefault();
        dragCounter.current = 0;
        setIsDragging(false);
        const files = Array.from(e.dataTransfer.files ?? []);
        if (files.length === 0)
            return;
        const accepted = files.filter((f) => f.size <= maxAttachmentFileSize);
        const rejected = files.filter((f) => f.size > maxAttachmentFileSize);
        if (rejected.length > 0) {
            setSelectionError(`Skipped ${rejected.length} oversized file${rejected.length === 1 ? "" : "s"}: ${rejected.map((f) => f.name).join(", ")}`);
        }
        else {
            setSelectionError(null);
        }
        if (accepted.length > 0) {
            setDroppedFiles(accepted);
            setIsUploaderOpen(true);
        }
    }, []);
    const removeAttachment = (attachment) => {
        setModal({
            type: "remove",
            value: attachment,
        });
    };
    const editAttachment = (file) => {
        setEditingAttachment(file);
        setIsEditorOpen(true);
    };
    const submit = (type, value) => {
        setModal({});
        if (type === "remove") {
            if (value.id !== undefined) {
                const data = {
                    id: value.id,
                };
                executeDelete(data, "DELETE");
            }
        }
    };
    const columns = tableColumns(attachmentType, isInAdminTools);
    const csvColumns = columns
        .filter((column) => column.key !== "tools")
        .map((column) => ({
        header: column.label,
        getValue: (file) => {
            const raw = file[String(column.key)];
            if ((0, isNullish_1.isNullish)(raw)) {
                return "";
            }
            switch (column.key) {
                case "name":
                    return String(file.name ?? "");
                case "period_start":
                case "period_end":
                case "date": {
                    const date = new Date(String(raw));
                    return isNaN(date.getTime()) ? String(raw) : (0, timeUtils_1.formatDate)(date, true);
                }
                case "port":
                    if ((0, compliance_pool_types_1.isBunkerDeliveryNote)(file)) {
                        return file.port?.name ?? "";
                    }
                    return "";
                case "energy_mj":
                case "intensity":
                case "balance_tco2e":
                case "available_tco2e":
                case "amount_t":
                case "price_per_ton":
                case "bio_premium_per_ton":
                    if (typeof raw === "number") {
                        const digits = Number.isInteger(raw) ? undefined : 2;
                        return (0, amountUtils_1.formatNumber)(raw, digits) ?? String(raw);
                    }
                    return String(raw);
                case "periodical_statement_id":
                    return (periodicalStatementOptions?.find((o) => o.value === Number(raw))
                        ?.label ?? String(raw));
                case "proof_of_sustainability_notes":
                    return Array.isArray(raw)
                        ? raw
                            .map((note) => note.name
                            ? note.name
                            : String(note))
                            .join(", ")
                        : String(raw);
                default:
                    return String(raw);
            }
        },
    }));
    return ((0, jsx_runtime_1.jsxs)("div", { children: [modal.type && ((0, jsx_runtime_1.jsx)(Modal_1.Modal, { type: "transactionAttachmentFile", isOpen: modal.type !== undefined, closeModal: () => setModal({}), modalType: modal.type, value: modal.value, submit: submit })), isInAdminTools && ((0, jsx_runtime_1.jsx)(TransactionAttachmentUploader_1.default, { selectedYear: selectedYear, shipId: shipId, attachmentType: attachmentType, refresh: refresh, periodicalStatementOptions: periodicalStatementOptions, initialFiles: droppedFiles, isOpen: isUploaderOpen, onClose: () => {
                    setIsUploaderOpen(false);
                    setDroppedFiles([]);
                }, onOpen: () => setIsUploaderOpen(true) })), selectionError && (0, jsx_runtime_1.jsx)(SelectionError, { children: selectionError }), editingAttachment && ((0, jsx_runtime_1.jsx)(TransactionAttachmentEditor_1.default, { attachmentData: editingAttachment, attachmentType: attachmentType, periodicalStatementOptions: periodicalStatementOptions, isOpen: isEditorOpen, onClose: () => {
                    setIsEditorOpen(false);
                    setEditingAttachment(null);
                }, refresh: refresh })), (0, jsx_runtime_1.jsx)(common_styled_components_1.Row, { children: (0, jsx_runtime_1.jsx)(ExportCsvButton_1.ExportCsvButton, { rows: transactionAttachments, columns: csvColumns, tableName: "transaction-attachments" }) }), (0, jsx_runtime_1.jsxs)(TableWrapper, { onDragEnter: isInAdminTools ? handleDragEnter : undefined, onDragOver: isInAdminTools ? handleDragOver : undefined, onDragLeave: isInAdminTools ? handleDragLeave : undefined, onDrop: isInAdminTools ? handleDrop : undefined, children: [(0, jsx_runtime_1.jsx)(DropOverlay, { "$visible": isDragging, children: (0, jsx_runtime_1.jsx)(DropOverlayText, { children: "Drop files to add attachment" }) }), transactionAttachments.length ? ((0, jsx_runtime_1.jsxs)(common_styled_components_1.Table, { children: [(0, jsx_runtime_1.jsx)("thead", { children: (0, jsx_runtime_1.jsx)("tr", { children: columns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.TH, { children: c.label }, c.key))) }) }), (0, jsx_runtime_1.jsx)("tbody", { children: transactionAttachments.map((transactionAttachment) => {
                                    return ((0, jsx_runtime_1.jsx)(common_styled_components_1.TR, { children: columns.map((c) => ((0, jsx_runtime_1.jsx)(common_styled_components_1.TD, { children: (0, jsx_runtime_1.jsx)(CellContent, { contentKey: c.key, file: transactionAttachment, removeAttachment: removeAttachment, editAttachment: editAttachment, isInAdminTools: isInAdminTools, periodicalStatementOptions: periodicalStatementOptions }) }, c.key))) }, transactionAttachment.id));
                                }) })] })) : ((0, jsx_runtime_1.jsx)("div", { children: (0, jsx_runtime_1.jsx)(common_styled_components_1.P, { children: "No files uploaded" }) }))] })] }));
};
exports.TransactionAttachmentTable = TransactionAttachmentTable;
